import { keyframes, type Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { ASSISTANT_AVATAR } from "../../../theme/tokens";
import { AssistantAvatarMood } from "./assistantAvatarConfig";

/** Class names the parts put on the SVG groups that move; the root's sx animates them per mood. */
export const AVATAR_PART = {
	BODY: "assistant-avatar-body",
	EYES: "assistant-avatar-eyes",
	PUPILS: "assistant-avatar-pupils",
	MOUTH: "assistant-avatar-mouth",
	WAVE: "assistant-avatar-wave",
	POINT_LEFT: "assistant-avatar-point-left",
	POINT_RIGHT: "assistant-avatar-point-right",
	THINKING_DOT: "assistant-avatar-thinking-dot",
	SPARKLE: "assistant-avatar-sparkle",
} as const;

type AvatarPart = (typeof AVATAR_PART)[keyof typeof AVATAR_PART];
type MotionRules = SystemStyleObject<Theme>;

const part = (name: AvatarPart): string => `& .${name}`;
const { MOTION } = ASSISTANT_AVATAR;

const blink = keyframes`
	0%, 95%, 100% { transform: scaleY(1); }
	97% { transform: scaleY(0.08); }
`;
const ponder = keyframes`
	0%, 100% { transform: rotate(-2deg); }
	50% { transform: rotate(2deg); }
`;
const dotPulse = keyframes`
	0%, 100% { opacity: 0.25; }
	50% { opacity: 1; }
`;
const talk = keyframes`
	from { transform: scaleY(0.35); }
	to { transform: scaleY(1.15); }
`;
const hop = keyframes`
	0%, 100% { transform: translateY(0) scale(1, 1); }
	20% { transform: translateY(0) scale(1.06, 0.94); }
	45% { transform: translateY(-8px) scale(0.96, 1.05); }
	75% { transform: translateY(0) scale(1.03, 0.97); }
`;
// One iteration that swings a few times and comes back to rest, so the hand never snaps into place.
const wave = keyframes`
	0%, 100% { transform: rotate(0deg); }
	15% { transform: rotate(-18deg); }
	40% { transform: rotate(16deg); }
	65% { transform: rotate(-14deg); }
	85% { transform: rotate(10deg); }
`;
const jump = keyframes`
	0%, 100% { transform: translateY(0); }
	35% { transform: translateY(-9px) rotate(-2deg); }
	65% { transform: translateY(0) scale(1.04, 0.96); }
`;
// Starts from a partial scale (never from 0) and is invisible at both ends, so it leaves nothing behind.
const sparklePop = keyframes`
	0% { transform: scale(0.5); opacity: 0; }
	30% { transform: scale(1.1); opacity: 1; }
	70% { opacity: 1; }
	100% { transform: scale(0.6); opacity: 0; }
`;

// Screen-side pushes toward the target; the avatar is never mirrored, so left always means screen-left.
const nudgeLeft = keyframes`
	0%, 100% { transform: translateX(0); }
	50% { transform: translateX(-4px); }
`;
const nudgeRight = keyframes`
	0%, 100% { transform: translateX(0); }
	50% { transform: translateX(4px); }
`;
const POINT_NUDGE_ITERATIONS = 2;

const THINKING_DOT_COUNT = 3;
const SPARKLE_COUNT = 4;

/** Per-sibling animation delays, so dots and sparkles run in sequence rather than together. */
const staggered = (name: AvatarPart, count: number, stepMs: number): Record<string, { animationDelay: string }> => {
	const rules: Record<string, { animationDelay: string }> = {};
	for (let index = 1; index < count; index++) {
		rules[`${part(name)}:nth-of-type(${index + 1})`] = { animationDelay: `${index * stepMs}ms` };
	}
	return rules;
};

const BLINK_RULES: MotionRules = { [part(AVATAR_PART.EYES)]: { animation: `${blink} ${MOTION.BLINK}ms infinite` } };

const THINKING_RULES: MotionRules = {
	...BLINK_RULES,
	[part(AVATAR_PART.BODY)]: { animation: `${ponder} ${MOTION.PONDER}ms ${MOTION.EASE_LOOP} infinite` },
	[part(AVATAR_PART.THINKING_DOT)]: { animation: `${dotPulse} ${MOTION.THINKING_DOT}ms ${MOTION.EASE_LOOP} infinite` },
	...staggered(AVATAR_PART.THINKING_DOT, THINKING_DOT_COUNT, MOTION.THINKING_DOT_STAGGER),
};

const DONE_RULES: MotionRules = {
	[part(AVATAR_PART.BODY)]: { animation: `${jump} ${MOTION.JUMP}ms ${MOTION.EASE_ONE_SHOT} 1` },
	[part(AVATAR_PART.SPARKLE)]: { animation: `${sparklePop} ${MOTION.SPARKLE}ms ${MOTION.EASE_ONE_SHOT} 1 both` },
	...staggered(AVATAR_PART.SPARKLE, SPARKLE_COUNT, MOTION.SPARKLE_STAGGER),
};

/** What moves in each mood. Only thinking and talking loop, because they show work in progress. */
const ANIMATED_RULES: Record<AssistantAvatarMood, MotionRules> = {
	[AssistantAvatarMood.Idle]: BLINK_RULES,
	[AssistantAvatarMood.Thinking]: THINKING_RULES,
	[AssistantAvatarMood.Talking]: {
		...BLINK_RULES,
		[part(AVATAR_PART.MOUTH)]: { animation: `${talk} ${MOTION.TALK}ms ${MOTION.EASE_LOOP} infinite alternate` },
	},
	[AssistantAvatarMood.NeedsYou]: {
		...BLINK_RULES,
		[part(AVATAR_PART.BODY)]: { animation: `${hop} ${MOTION.HOP}ms ${MOTION.EASE_ONE_SHOT} 1` },
		[part(AVATAR_PART.WAVE)]: { animation: `${wave} ${MOTION.WAVE}ms ${MOTION.EASE_LOOP} 1` },
	},
	[AssistantAvatarMood.Done]: DONE_RULES,
	[AssistantAvatarMood.Oops]: {},
};

/** Held poses — these hold with or without animation, since they carry the mood in a still frame. */
const POSE_RULES: Record<AssistantAvatarMood, MotionRules> = {
	[AssistantAvatarMood.Idle]: {},
	[AssistantAvatarMood.Thinking]: { [part(AVATAR_PART.PUPILS)]: { transform: "translate(2.6px, -3.2px)" } },
	[AssistantAvatarMood.Talking]: {},
	[AssistantAvatarMood.NeedsYou]: {},
	[AssistantAvatarMood.Done]: {},
	[AssistantAvatarMood.Oops]: { [part(AVATAR_PART.BODY)]: { transform: "rotate(-5deg)" } },
};

// SVG groups default to rotating around the canvas origin; these pin each moving part to its own pivot.
const PIVOT_RULES: MotionRules = {
	[part(AVATAR_PART.BODY)]: { transformBox: "view-box", transformOrigin: "60px 110px" },
	[`${part(AVATAR_PART.EYES)}, ${part(AVATAR_PART.MOUTH)}, ${part(AVATAR_PART.SPARKLE)}`]: {
		transformBox: "fill-box",
		transformOrigin: "center",
	},
	[part(AVATAR_PART.WAVE)]: { transformBox: "fill-box", transformOrigin: "20% 90%" },
	[part(AVATAR_PART.SPARKLE)]: { opacity: 0 },
};

const REDUCED_MOTION_RULES: MotionRules = {
	"@media (prefers-reduced-motion: reduce)": { "&, & *": { animation: "none !important", transition: "none !important" } },
};

const STILL_RULES: MotionRules = {};

// An sx array, not a spread: the entries share selectors, and a spread would drop the earlier ones' properties.
const buildMotionSx = (mood: AssistantAvatarMood, animated: boolean): MotionRules[] => [
	PIVOT_RULES,
	POSE_RULES[mood],
	animated ? ANIMATED_RULES[mood] : STILL_RULES,
	REDUCED_MOTION_RULES,
];

// Built once per mood so a re-render hands emotion the same objects.
type MotionSxPair = { animated: MotionRules[]; still: MotionRules[] };
const motionSxPair = (mood: AssistantAvatarMood): MotionSxPair => ({ animated: buildMotionSx(mood, true), still: buildMotionSx(mood, false) });
const MOTION_SX: Record<AssistantAvatarMood, MotionSxPair> = {
	[AssistantAvatarMood.Idle]: motionSxPair(AssistantAvatarMood.Idle),
	[AssistantAvatarMood.Thinking]: motionSxPair(AssistantAvatarMood.Thinking),
	[AssistantAvatarMood.Talking]: motionSxPair(AssistantAvatarMood.Talking),
	[AssistantAvatarMood.NeedsYou]: motionSxPair(AssistantAvatarMood.NeedsYou),
	[AssistantAvatarMood.Done]: motionSxPair(AssistantAvatarMood.Done),
	[AssistantAvatarMood.Oops]: motionSxPair(AssistantAvatarMood.Oops),
};

export const avatarMotionSx = (mood: AssistantAvatarMood, animated: boolean): MotionRules[] => (animated ? MOTION_SX[mood].animated : MOTION_SX[mood].still);

/** Custom properties on the root svg that carry the gaze offset, so a new target restyles nothing but these two values. */
export const GAZE_VAR = {
	X: "--assistant-avatar-gaze-x",
	Y: "--assistant-avatar-gaze-y",
} as const;

// Comes after the mood pose in the sx array, so a gaze overrides Thinking's fixed glance.
const GAZE_RULES: MotionRules = { [part(AVATAR_PART.PUPILS)]: { transform: `translate(var(${GAZE_VAR.X}), var(${GAZE_VAR.Y}))` } };
const GAZE_TRANSITION_RULES: MotionRules = {
	[part(AVATAR_PART.PUPILS)]: { transition: `transform ${MOTION.GAZE}ms ${MOTION.EASE_ONE_SHOT}` },
};
const POINT_NUDGE_RULES: MotionRules = {
	[part(AVATAR_PART.POINT_LEFT)]: { animation: `${nudgeLeft} ${MOTION.POINT_NUDGE}ms ${MOTION.EASE_LOOP} ${POINT_NUDGE_ITERATIONS}` },
	[part(AVATAR_PART.POINT_RIGHT)]: { animation: `${nudgeRight} ${MOTION.POINT_NUDGE}ms ${MOTION.EASE_LOOP} ${POINT_NUDGE_ITERATIONS}` },
};
const NO_GUIDE_RULES: MotionRules[] = [];
const GAZE_ANIMATED_SX: MotionRules[] = [GAZE_RULES, GAZE_TRANSITION_RULES];
const GAZE_STILL_SX: MotionRules[] = [GAZE_RULES];
const POINT_ANIMATED_SX: MotionRules[] = [POINT_NUDGE_RULES];

/** Tour-guide styles, appended after `avatarMotionSx`; keyed on whether gaze/point are set, not their values, so the class stays stable. */
export const avatarGuideSx = (gazing: boolean, pointing: boolean, animated: boolean): MotionRules[] => {
	const gaze = gazing ? (animated ? GAZE_ANIMATED_SX : GAZE_STILL_SX) : NO_GUIDE_RULES;
	const point = pointing && animated ? POINT_ANIMATED_SX : NO_GUIDE_RULES;
	return [...gaze, ...point];
};
